import { type FC, type MouseEventHandler, useEffect, useRef } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classnames from 'classnames';

import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import Close from 'dibs-icons/exports/legacy/Close';

import { useHeadlineContext } from '../PhotosSearchBrowseHeadlineContext';
import { useServerVars } from '../../shared/ServerVarsContext';

import { type PhotoItemQuickView_photo$data } from './__generated__/PhotoItemQuickView_photo.graphql';

import styles from './PhotoItemQuickView.scss';

const messages = defineMessages({
    photoByline: {
        id: 'PhotoItemQuickView.photoByline',
        defaultMessage: 'Photography: {photographer}',
    },
    byline: {
        id: 'PhotoItemQuickView.byline',
        defaultMessage: '{projectType} by {designFirm}',
    },
    closeLabel: {
        id: 'PhotoItemQuickView.closeLabel',
        defaultMessage: 'Close Quick View',
    },
    // location requires BE work on photos solr search before it can added here
    // location: {
    //     id: 'PhotoItemQuickView.location',
    //     defaultMessage: 'Located in {location}',
    // },
    cta: {
        id: 'PhotoItemQuickView.cta',
        defaultMessage: 'View Full Details',
    },
});

type Props = {
    photo: PhotoItemQuickView_photo$data;
    open?: boolean;
    href: string;
    onClose: () => void;
    onClick: MouseEventHandler;
};

const PhotoItemQuickViewComponent: FC<Props> = ({ photo, open, href, onClick, onClose }) => {
    const intl = useIntl();
    const cardBackRef = useRef<HTMLDivElement>(null);
    const headline = useHeadlineContext();
    const { isMobile } = useServerVars();
    const project = `${photo?.styles?.map(styleObj => styleObj?.name)?.join(' ')} ${
        photo?.room?.name
    } - ${photo?.project?.name}`;

    const byline = intl.formatMessage(messages.byline, {
        projectType: photo?.project?.projectTypes?.join(' '),
        designFirm: photo?.designFirm?.name,
    });
    const photoByline = intl.formatMessage(messages.photoByline, { photographer: photo?.caption });
    // const location = intl.formatMessage(messages.location, { location: 'foo' });

    useEffect(() => {
        if (open) {
            cardBackRef.current?.focus();
        }
    }, [open]);

    return (
        <div
            className={classnames(styles.cardBack, { [styles.hidden]: !open })}
            // add tabindex to provide focus target when toggling between "front" (photo
            // tile) and "back" (quick view) of card
            tabIndex={-1}
            ref={cardBackRef}
        >
            <Link
                onClick={onClose}
                className={styles.closeButton}
                ariaLabel={intl.formatMessage(messages.closeLabel)}
            >
                <Close />
            </Link>
            <p className={styles.quickViewContent}>
                <span className={styles.title}>{project}</span>
                {/* <span className={styles.location}>{location}</span> */}
                {byline && <span className={styles.summary}>{byline}</span>}
                <span className={styles.summary}>{headline}</span>
                {photoByline && <span className={styles.summary}>{photoByline}</span>}
            </p>
            {isMobile ? (
                <Link href={href} onClick={onClick}>
                    {intl.formatMessage(messages.cta)}
                </Link>
            ) : (
                <Button fullWidth type="secondary" size="small" href={href} onClick={onClick}>
                    {intl.formatMessage(messages.cta)}
                </Button>
            )}
        </div>
    );
};

export const PhotoItemQuickView = createFragmentContainer(PhotoItemQuickViewComponent, {
    photo: graphql`
        fragment PhotoItemQuickView_photo on PhotosSearchBrowsePhoto {
            caption
            styles {
                name
            }
            room {
                name
            }
            designFirm {
                name
            }
            project {
                projectTypes
                name
            }
        }
    `,
});
